import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { tournaments } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Trophy, ArrowLeft, RotateCcw, Save, ExternalLink,
  Calendar, MapPin, Users, FileText, AlertTriangle, Eye, Check, X,
  ListOrdered, Bell, Link2, Monitor, Search, ImageIcon, Repeat,
} from 'lucide-react';
import { GAMES, FORMATS, getGameColor } from '../constants/theme';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import BannerEditor from '../components/BannerEditor';

// ── Game logo mapping ──
const GAME_LOGOS = {
  magic: '/game-logos/magic.png', pokemon: '/game-logos/pokemon.png',
  yugioh: '/game-logos/yugioh.png', lorcana: '/game-logos/lorcana.png',
  onepiece: '/game-logos/onepiece.png', dbs: '/game-logos/dragonball.png',
  altered: '/game-logos/altered.png', fab: '/game-logos/flesh-and-blood.png',
  riftbound: '/game-logos/riftbound.png', starwars: '/game-logos/starwars.png',
  sorcery: '/game-logos/sorcery.png',
};

// ── Editor sites per game ──
const EDITOR_SITES = {
  magic: { name: 'Wizards EventLink', url: 'https://eventlink.wizards.com', iframe: true },
  pokemon: { name: 'Pokémon Organized Play', url: 'https://events.pokemon.com', iframe: false },
  yugioh: { name: 'Konami Card Game Network', url: 'https://www.konami.com/yugioh/card-game-network/', iframe: false },
  fab: { name: 'GEM (Flesh and Blood)', url: 'https://gem.fabtcg.com', iframe: false },
  lorcana: { name: 'Lorcana OP', url: 'https://op.disneylorcana.com', iframe: false },
  onepiece: { name: 'One Piece OP', url: 'https://onepiece-cardgame.com', iframe: false },
  starwars: { name: 'Star Wars OP', url: 'https://starwarsunlimited.com/organized-play', iframe: false },
};

// ── Time slots (15 min intervals) ──
const TIME_SLOTS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    TIME_SLOTS.push({ value: `${hh}:${mm}`, label: `${hh}h${mm}` });
  }
}

export default function CreateTournamentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, selectedShop } = useAuth();
  const [step, setStep] = useState('game');
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showCodeWarning, setShowCodeWarning] = useState(false);
  const [showNoLimitWarning, setShowNoLimitWarning] = useState(false);
  const [showExceedWarning, setShowExceedWarning] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const [bannerBlob, setBannerBlob] = useState(null);
  const [coverDataUrl, setCoverDataUrl] = useState(null);

  const [form, setForm] = useState({
    name: '', game: '', format: '', customFormat: '', description: '',
    customGame: '',
    date: '', time: '14:00', end_date: '', end_time: '',
    venue_name: user?.company_name || '',
    venue_address: user?.address || '',
    venue_city: user?.city || '',
    venue_postal: '',
    no_physical_venue: false,
    capacity: '',
    refund_type: 'hours',
    refund_hours: 24,
    price: 0,
    tournament_code: '',
    waitlist_mode: 'manual',
    waitlist_confirm_minutes: 30,
    online_registration: true,
    is_recurring: false,
    recurrence_rule: 'weekly',
    recurrence_end_date: '',
    publish_now: true,
    publish_at: '',
  });

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      venue_name: prev.venue_name || selectedShop?.name || user?.company_name || '',
      venue_address: prev.venue_address || selectedShop?.address || user?.address || '',
      venue_city: prev.venue_city || selectedShop?.address_data?.city || user?.city || '',
      venue_postal: prev.venue_postal || selectedShop?.address_data?.postcode || selectedShop?.address_data?.postal_code || '',
      waitlist_mode: prev._waitlistTouched ? prev.waitlist_mode : (selectedShop?.waitlist_mode === 'auto_push' ? 'notification' : (selectedShop?.waitlist_mode || prev.waitlist_mode)),
      waitlist_confirm_minutes: prev._waitlistTouched ? prev.waitlist_confirm_minutes : (selectedShop?.waitlist_confirm_minutes || prev.waitlist_confirm_minutes),
    }));
  }, [user, selectedShop]);

  // Handle duplicate pre-fill
  useEffect(() => {
    if (searchParams.get('duplicate') === 'true') {
      try {
        const raw = sessionStorage.getItem('duplicate_tournament');
        if (raw) {
          const dup = JSON.parse(raw);
          sessionStorage.removeItem('duplicate_tournament');
          setForm(prev => ({
            ...prev,
            ...dup,
            // Ensure dates and code are blank
            date: '', time: '14:00', end_date: '', end_time: '',
            tournament_code: '',
          }));
          // If game is set, skip to form step
          if (dup.game) setStep('form');
        }
      } catch {}
    }
  }, [searchParams]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setWaitlist = (v) => setForm(prev => ({ ...prev, waitlist_mode: v, _waitlistTouched: true }));
  const rawFormats = form.game ? (FORMATS[form.game] || []) : [];
  const formats = [...rawFormats, 'Autre (préciser)'];
  const gameInfo = GAMES.find(g => g.id === form.game);
  const editorSite = form.game ? EDITOR_SITES[form.game] : null;
  const accentColor = getGameColor(form.game);

  const getRefundLabel = () => {
    if (form.refund_type === 'never') return 'Jamais';
    if (form.refund_type === 'ask_admin') return "Toujours envoyer une demande à l'admin de la boutique";
    return `Remboursement automatique si annulation ${form.refund_hours}h avant le début du tournoi`;
  };

  const resetForm = () => {
    setForm(prev => ({
      name: '', game: prev.game, format: '', customFormat: '', description: '',
      customGame: prev.customGame,
      date: '', time: '14:00', end_date: '', end_time: '',
      venue_name: selectedShop?.name || user?.company_name || '', venue_address: selectedShop?.address || user?.address || '',
      venue_city: selectedShop?.address_data?.city || user?.city || '', venue_postal: selectedShop?.address_data?.postcode || '', no_physical_venue: false,
      capacity: '', refund_type: 'hours', refund_hours: 24, price: 0,
      tournament_code: '', waitlist_mode: 'manual', waitlist_confirm_minutes: 30, online_registration: true, publish_now: true, publish_at: '',
    }));
    toast.info('Champs réinitialisés');
  };

  const handleSubmit = () => {
    if (!form.name || !form.date) {
      toast.error('Veuillez remplir les champs obligatoires (nom, date)');
      return;
    }
    if (form.game === 'autre' && !form.customGame.trim()) {
      toast.error('Veuillez préciser le jeu');
      return;
    }
    if (form.is_recurring && !form.recurrence_end_date) {
      toast.error('Veuillez indiquer une date de fin de récurrence');
      return;
    }
    const cap = form.capacity ? parseInt(form.capacity) : null;
    const roomCap = selectedShop?.room_capacity;
    // No player limit set -> confirm
    if (!cap) { setShowNoLimitWarning(true); return; }
    // Player limit exceeds the room capacity -> confirm
    if (roomCap && cap > roomCap) { setShowExceedWarning(true); return; }
    // If no code, ask confirmation via custom modal (not window.confirm)
    if (!form.tournament_code?.trim()) {
      setShowCodeWarning(true);
      return;
    }
    setShowPreview(true);
  };

  // Continue after a capacity-related confirmation popup ("OK")
  const proceedAfterCapacity = () => {
    setShowNoLimitWarning(false);
    setShowExceedWarning(false);
    if (!form.tournament_code?.trim()) { setShowCodeWarning(true); return; }
    setShowPreview(true);
  };

  // "Modifier" -> close popup and focus the capacity field
  const editCapacity = () => {
    setShowNoLimitWarning(false);
    setShowExceedWarning(false);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="tournament-capacity-input"]');
      if (el) { el.focus(); el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    }, 50);
  };

  const proceedToPreview = () => {
    setShowCodeWarning(false);
    setShowPreview(true);
  };

  const confirmCreate = async () => {
    setLoading(true);
    try {
      const finalFormat = form.format === 'Autre (préciser)' ? form.customFormat : form.format;
      const finalGame = form.game === 'autre' ? form.customGame : form.game;
      const startDateTime = form.date && form.time ? `${form.date}T${form.time}:00` : form.date;
      const endDateTime = form.end_date && form.end_time ? `${form.end_date}T${form.end_time}:00` : form.end_date || null;

      // Upload banner if available
      let bannerUrl = null;
      if (bannerBlob) {
        try {
          const formData = new FormData();
          formData.append('file', bannerBlob, 'banner.jpg');
          const uploadRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/shop-asset`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
            body: formData,
          });
          if (uploadRes.ok) {
            const uploadData = await uploadRes.json();
            bannerUrl = uploadData.url;
          }
        } catch (e) { console.warn('Banner upload failed:', e); }
      }

      // Upload the raw cover image (for Instagram story background)
      let coverImageUrl = null;
      if (coverDataUrl) {
        try {
          const blob = await (await fetch(coverDataUrl)).blob();
          const fd = new FormData();
          fd.append('file', blob, 'cover.jpg');
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/shop-asset`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
            body: fd,
          });
          if (res.ok) coverImageUrl = (await res.json()).url;
        } catch (e) { console.warn('Cover upload failed:', e); }
      }

      const payload = {
        name: form.name, game: finalGame, format: finalFormat,
        description: form.description,
        date: new Date(startDateTime).toISOString(),
        end_date: endDateTime ? new Date(endDateTime).toISOString() : null,
        price: parseFloat(form.price) || 0,
        capacity: form.capacity ? parseInt(form.capacity) : null,
        venue: form.no_physical_venue ? { name: 'En ligne', address: '', city: '' } : {
          name: form.venue_name, address: form.venue_address,
          city: form.venue_city, postal_code: form.venue_postal,
        },
        refund_policy: form.refund_type === 'hours' ? `hours_${form.refund_hours}` : form.refund_type,
        tournament_codes: form.tournament_code ? { '0': form.tournament_code } : {},
        waitlist_mode: form.waitlist_mode,
        waitlist_confirm_minutes: form.waitlist_mode === 'automatic' ? (parseInt(form.waitlist_confirm_minutes) || 30) : 30,
        online_registration: form.online_registration !== false,
        is_recurring: !!form.is_recurring,
        recurrence_rule: form.is_recurring ? form.recurrence_rule : null,
        recurrence_start_date: form.is_recurring ? new Date(startDateTime).toISOString() : null,
        recurrence_end_date: (form.is_recurring && form.recurrence_end_date) ? new Date(`${form.recurrence_end_date}T23:59:00`).toISOString() : null,
        banner_url: bannerUrl,
        cover_image_url: coverImageUrl,
        publish_at: (!form.publish_now && form.publish_at) ? new Date(form.publish_at).toISOString() : null,
        shop_id: selectedShop?.id || null,
      };
      await tournaments.create(payload);
      toast.success('Tournoi créé avec succès !');
      navigate('/agenda');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
      setShowPreview(false);
    }
  };

  const fmtDate = (d) => {
    try { return new Date(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  const getGmapUrl = () => {
    const q = [form.venue_name, form.venue_address, form.venue_postal, form.venue_city].filter(Boolean).join(', ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  };

  // ====== STEP 1: Game Selection ======
  if (step === 'game') {
    return (
      <div className="max-w-4xl mx-auto" data-testid="game-selection">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-white">Créer un tournoi</h1>
            <p className="text-[#666] mt-1">Sélectionnez le jeu pour commencer</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {GAMES.map(game => {
            const logo = GAME_LOGOS[game.id];
            return (
              <button key={game.id} data-testid={`game-select-${game.id}`}
                onClick={() => { set('game', game.id); set('format', ''); setStep('form'); }}
                className="group text-left">
                <Card className="bg-[#1a1a1a] border-2 border-[#333] hover:border-current transition-all h-full cursor-pointer"
                  onMouseEnter={e => e.currentTarget.style.borderColor = game.color}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#333'}>
                  <div className="h-1.5 w-full rounded-t-lg" style={{ backgroundColor: game.color }} />
                  <CardContent className="p-4 flex flex-col items-center text-center">
                    {logo ? (
                      <div className="w-16 h-16 flex items-center justify-center mb-3">
                        <img src={logo} alt={game.name} className="max-w-full max-h-full object-contain" />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${game.color}15` }}>
                        <Trophy className="w-7 h-7" style={{ color: game.color }} />
                      </div>
                    )}
                    <span className="text-sm font-bold text-white">{game.shortName || game.name}</span>
                    {EDITOR_SITES[game.id] && (
                      <span className="text-[10px] text-[#666] mt-1">{EDITOR_SITES[game.id].iframe ? 'Iframe dispo' : 'Lien externe'}</span>
                    )}
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ====== STEP 2: Form ======
  const ActionButtons = () => (
    <div className="flex flex-wrap gap-3">
      <Button variant="outline" className="border-[#333] text-[#B0B0B0] gap-2" data-testid="save-template-btn" onClick={() => toast.info('Fonctionnalité bientôt disponible')}>
        <Save className="w-4 h-4" /> Enregistrer comme modèle
      </Button>
      <Button onClick={handleSubmit} disabled={loading} className="gap-2" style={{ backgroundColor: accentColor }}
        data-testid="create-tournament-btn">
        <Trophy className="w-4 h-4" /> {loading ? 'Création...' : 'Créer le tournoi'}
      </Button>
      <Button variant="outline" onClick={resetForm} className="border-[#333] text-[#666] gap-2" data-testid="reset-form-btn">
        <RotateCcw className="w-4 h-4" /> Réinitialiser
      </Button>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="create-tournament-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => setStep('game')} className="text-white" data-testid="back-to-game">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white">Créer un tournoi</h1>
            {GAME_LOGOS[form.game] && <img src={GAME_LOGOS[form.game]} alt={gameInfo?.name} className="h-8 object-contain" />}
            <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: `${accentColor}20`, color: accentColor }}>
              {form.game === 'autre' ? 'Autre' : gameInfo?.shortName || form.game}
            </span>
          </div>
        </div>
      </div>

      <ActionButtons />

      {/* Custom game name */}
      {form.game === 'autre' && (
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader><CardTitle className="text-white">Préciser le jeu</CardTitle></CardHeader>
          <CardContent>
            <Input value={form.customGame} onChange={e => set('customGame', e.target.value)}
              placeholder="Nom du jeu..." className="bg-[#252525] border-[#333] text-white" data-testid="custom-game-input" />
          </CardContent>
        </Card>
      )}

      {/* Format */}
      {formats.length > 0 && (
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><Trophy className="w-5 h-5" style={{ color: accentColor }} /> Format</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {formats.map(f => (
                <button key={f} type="button" onClick={() => set('format', f)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all ${form.format === f ? 'text-white' : 'border-[#333] bg-[#0a0a0a] text-[#B0B0B0] hover:border-[#555]'}`}
                  style={form.format === f ? { borderColor: accentColor, backgroundColor: `${accentColor}20`, color: accentColor } : {}}>
                  {f}
                </button>
              ))}
            </div>
            {form.format === 'Autre (préciser)' && (
              <Input value={form.customFormat} onChange={e => set('customFormat', e.target.value)}
                placeholder="Préciser le format..." className="bg-[#252525] border-[#333] text-white mt-3" data-testid="custom-format-input" />
            )}
          </CardContent>
        </Card>
      )}

      {/* General Info */}
      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><FileText className="w-5 h-5" style={{ color: accentColor }} /> Informations générales</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-[#B0B0B0]">Nom du tournoi *</Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Tournoi Standard du vendredi"
              className="bg-[#252525] border-[#333] text-white" data-testid="tournament-name-input" />
          </div>

          {/* Date + Time (15 min intervals) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[#B0B0B0]">Date *</Label>
                <Input type="date" value={form.date} onChange={e => set('date', e.target.value)}
                  className="bg-[#252525] border-[#333] text-white" data-testid="tournament-date-input" />
              </div>
              <div>
                <Label className="text-[#B0B0B0]">Heure *</Label>
                <Select value={form.time} onValueChange={v => set('time', v)}>
                  <SelectTrigger className="bg-[#252525] border-[#333] text-white" data-testid="tournament-time-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#252525] border-[#333] max-h-[250px]">
                    {TIME_SLOTS.map(t => (
                      <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[#B0B0B0]">Date de fin <span className="text-[#666]">(opt.)</span></Label>
                <Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)}
                  className="bg-[#252525] border-[#333] text-white" />
              </div>
              <div>
                <Label className="text-[#B0B0B0]">Heure de fin <span className="text-[#666]">(opt.)</span></Label>
                <Select value={form.end_time} onValueChange={v => set('end_time', v)}>
                  <SelectTrigger className="bg-[#252525] border-[#333] text-white">
                    <SelectValue placeholder="--h--" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#252525] border-[#333] max-h-[250px]">
                    {TIME_SLOTS.map(t => (
                      <SelectItem key={t.value} value={t.value} className="text-white">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#B0B0B0]">Prix d'entrée (€)</Label>
              <Input type="number" min="0" step="0.5" value={form.price} onChange={e => set('price', e.target.value)}
                className="bg-[#252525] border-[#333] text-white" data-testid="tournament-price-input" />
            </div>
            <div>
              <Label className="text-[#B0B0B0]">Joueurs max <span className="text-[#666]">(opt.)</span></Label>
              <Input type="number" min="4" value={form.capacity} onChange={e => set('capacity', e.target.value)}
                placeholder="Illimité si vide" className="bg-[#252525] border-[#333] text-white" data-testid="tournament-capacity-input" />
            </div>
          </div>

          {/* Tournament Code */}
          <div>
            <Label className="text-[#B0B0B0]">Code tournoi éditeur <span className="text-[#666]">(opt.)</span></Label>
            <div className="flex gap-3 items-start">
              <Input value={form.tournament_code} onChange={e => set('tournament_code', e.target.value)}
                placeholder="Ex: ABC123" className="bg-[#252525] border-[#333] text-white flex-1" data-testid="tournament-code-input" />
              {editorSite && (
                <div className="flex gap-2 shrink-0">
                  {editorSite.iframe && (
                    <Button variant="outline" size="sm" onClick={() => setShowIframe(!showIframe)}
                      className="border-[#333] text-white gap-1.5 text-xs" data-testid="toggle-iframe-btn">
                      {showIframe ? <X className="w-3.5 h-3.5" /> : <Monitor className="w-3.5 h-3.5" />}
                      {showIframe ? 'Fermer' : 'Iframe'}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => window.open(editorSite.url, '_blank')}
                    className="border-[#333] text-white gap-1.5 text-xs" data-testid="open-editor-btn">
                    <ExternalLink className="w-3.5 h-3.5" /> {editorSite.name}
                  </Button>
                </div>
              )}
            </div>
            {showIframe && editorSite?.iframe && (
              <div className="rounded-lg border border-[#333] overflow-hidden mt-3">
                <iframe src={editorSite.url} className="w-full bg-white" style={{ height: '500px' }}
                  title={editorSite.name} sandbox="allow-same-origin allow-scripts allow-forms allow-popups" data-testid="editor-iframe" />
              </div>
            )}
          </div>

          {/* Scheduled publishing */}
          <div className="bg-[#1f1f1f] border border-[#333] rounded-lg p-4 space-y-3" data-testid="publish-section">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="text-white font-medium">Publication programmée</Label>
                <p className="text-xs text-[#666] mt-0.5">Choisissez quand le tournoi devient visible publiquement.</p>
              </div>
              <button type="button" onClick={() => set('publish_now', !form.publish_now)} data-testid="publish-toggle"
                className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${!form.publish_now ? 'bg-[#FF6B35]' : 'bg-[#444]'}`}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${!form.publish_now ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {form.publish_now ? (
              <p className="text-xs text-[#4ECDC4]">Publié immédiatement — visible par tout le monde dès la création.</p>
            ) : (
              <div>
                <Label className="text-[#B0B0B0] text-sm">Date et heure de publication</Label>
                <Input type="datetime-local" value={form.publish_at} onChange={e => set('publish_at', e.target.value)}
                  className="bg-[#252525] border-[#333] text-white" data-testid="publish-at-input" />
                <p className="text-xs text-[#666] mt-1">Avant cette date, le tournoi reste visible uniquement par votre équipe (boutique) et les administrateurs.</p>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <Label className="text-[#B0B0B0]">Description</Label>
            <RichDescription value={form.description} onChange={v => set('description', v)} />
          </div>
        </CardContent>
      </Card>

      {/* Venue */}
      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><MapPin className="w-5 h-5" style={{ color: accentColor }} /> Lieu</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => set('no_physical_venue', !form.no_physical_venue)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                form.no_physical_venue ? 'text-white' : 'border-[#333] text-[#666] hover:border-[#555]'
              }`}
              style={form.no_physical_venue ? { borderColor: accentColor, backgroundColor: `${accentColor}20`, color: accentColor } : {}}
              data-testid="no-venue-toggle">
              <Monitor className="w-4 h-4" />
              {form.no_physical_venue ? 'Pas de lieu physique (en ligne)' : 'Ce tournoi n\'a pas de lieu physique'}
            </button>
          </div>
          {!form.no_physical_venue && (
            <>
              <div>
                <Input value={form.venue_name} onChange={e => set('venue_name', e.target.value)}
                  placeholder="Nom du lieu" className="bg-[#252525] border-[#333] text-white" data-testid="venue-name-input" />
              </div>
              <div>
                <Input value={form.venue_address} onChange={e => set('venue_address', e.target.value)}
                  placeholder="Adresse" className="bg-[#252525] border-[#333] text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input value={form.venue_postal} onChange={e => set('venue_postal', e.target.value)}
                    placeholder="Code postal" className="bg-[#252525] border-[#333] text-white" data-testid="venue-postal-input" />
                </div>
                <div>
                  <Input value={form.venue_city} onChange={e => set('venue_city', e.target.value)}
                    placeholder="Ville" className="bg-[#252525] border-[#333] text-white" data-testid="venue-city-input" />
                </div>
              </div>
              {(form.venue_address || form.venue_city) && (
                <a href={getGmapUrl()} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-[#333] text-[#B0B0B0] hover:text-white hover:border-[#555] transition-all">
                  <MapPin className="w-3.5 h-3.5" /> Voir sur Google Maps
                </a>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Recurrence */}
      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><Repeat className="w-5 h-5" style={{ color: accentColor }} /> Récurrence</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <button type="button" onClick={() => set('is_recurring', !form.is_recurring)} data-testid="recurrence-toggle"
            className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${form.is_recurring ? '' : 'border-[#333] bg-[#0a0a0a]'}`}
            style={form.is_recurring ? { borderColor: accentColor, backgroundColor: `${accentColor}10` } : {}}>
            <div className="flex items-center gap-3">
              <Repeat className="w-4 h-4" style={{ color: form.is_recurring ? accentColor : '#666' }} />
              <span className="text-white font-bold text-sm">Événement récurrent</span>
            </div>
            <div className={`w-11 h-6 rounded-full p-0.5 transition-colors ${form.is_recurring ? '' : 'bg-[#333]'}`} style={form.is_recurring ? { backgroundColor: accentColor } : {}}>
              <div className={`w-5 h-5 rounded-full bg-white transition-transform ${form.is_recurring ? 'translate-x-5' : ''}`} />
            </div>
          </button>
          {form.is_recurring && (
            <div className="grid sm:grid-cols-2 gap-4 pt-1" data-testid="recurrence-options">
              <div>
                <Label className="text-[#B0B0B0] text-sm mb-1.5 block">Fréquence</Label>
                <Select value={form.recurrence_rule} onValueChange={(v) => set('recurrence_rule', v)}>
                  <SelectTrigger className="bg-[#252525] border-[#333] text-white" data-testid="recurrence-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#333] text-white">
                    <SelectItem value="daily" className="text-white">Tous les jours</SelectItem>
                    <SelectItem value="weekly" className="text-white">Toutes les semaines</SelectItem>
                    <SelectItem value="biweekly" className="text-white">Toutes les 2 semaines</SelectItem>
                    <SelectItem value="monthly" className="text-white">Tous les mois</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[#666] text-xs mt-1.5">Début : la date du tournoi ({form.date ? new Date(form.date).toLocaleDateString('fr-FR') : 'à définir'}).</p>
              </div>
              <div>
                <Label className="text-[#B0B0B0] text-sm mb-1.5 block">Date de fin de récurrence *</Label>
                <Input type="date" value={form.recurrence_end_date} min={form.date || undefined}
                  onChange={(e) => set('recurrence_end_date', e.target.value)}
                  className="bg-[#252525] border-[#333] text-white" data-testid="recurrence-end-date" />
                <p className="text-[#666] text-xs mt-1.5">Les occurrences sont créées au fur et à mesure, jamais plus de 2 mois à l'avance.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Banner Editor */}
      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <ImageIcon className="w-5 h-5" style={{ color: accentColor }} />
            Bannière (3:1)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-[#666] text-xs mb-3">La bannière est générée automatiquement. Uploadez une image pour la partie droite.</p>
          <BannerEditor
            shopName={selectedShop?.name || ''}
            shopLogoUrl={selectedShop?.logo_url || ''}
            tournamentName={form.name || 'Nom du tournoi'}
            gameName={gameInfo?.name || ''}
            gameId={form.game || ''}
            format={form.format === 'Autre (préciser)' ? form.customFormat : form.format}
            address={form.no_physical_venue ? 'En ligne' : [form.venue_address, form.venue_postal, form.venue_city].filter(Boolean).join(', ')}
            dateStr={form.date ? new Date(`${form.date}T${form.time || '14:00'}:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : ''}
            defaultBgColor={selectedShop?.banner_bg_color || user?.banner_bg_color}
            onCoverImage={setCoverDataUrl}
            onBannerReady={setBannerBlob}
          />
        </CardContent>
      </Card>

      {/* Refund Policy */}
      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardHeader><CardTitle className="text-white">Politique de remboursement</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <Select value={form.refund_type} onValueChange={v => set('refund_type', v)}>
            <SelectTrigger className="bg-[#252525] border-[#333] text-white" data-testid="refund-policy-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#252525] border-[#333]">
              <SelectItem value="hours" className="text-white">Remboursement automatique si annulation X heures avant</SelectItem>
              <SelectItem value="never" className="text-white">Jamais</SelectItem>
              <SelectItem value="ask_admin" className="text-white">Toujours envoyer une demande à l'admin</SelectItem>
            </SelectContent>
          </Select>
          {form.refund_type === 'hours' && (
            <div className="flex items-center gap-3">
              <Label className="text-[#B0B0B0] whitespace-nowrap">Remboursement automatique si annulation</Label>
              <Input type="number" min="1" max="168" value={form.refund_hours}
                onChange={e => set('refund_hours', parseInt(e.target.value) || 24)}
                className="bg-[#252525] border-[#333] text-white w-20 text-center" data-testid="refund-hours-input" />
              <Label className="text-[#B0B0B0] whitespace-nowrap">heures avant le début</Label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Registration mode */}
      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><Link2 className="w-5 h-5" style={{ color: accentColor }} /> Inscriptions</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-[#666] text-sm">Comment les joueurs s'inscrivent-ils ?</p>
          <button type="button" onClick={() => set('online_registration', true)} data-testid="registration-online"
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${form.online_registration !== false ? '' : 'border-[#333] bg-[#0a0a0a]'}`}
            style={form.online_registration !== false ? { borderColor: accentColor, backgroundColor: `${accentColor}10` } : {}}>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0"
                style={{ borderColor: form.online_registration !== false ? accentColor : '#555' }}>
                {form.online_registration !== false && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accentColor }} />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" style={{ color: form.online_registration !== false ? accentColor : '#666' }} />
                  <span className="text-white font-bold text-sm">Autoriser les inscriptions en ligne</span>
                </div>
                <p className="text-[#888] text-xs mt-1 leading-relaxed">Les joueurs peuvent s'inscrire eux-mêmes directement sur Arcanapp.</p>
              </div>
            </div>
          </button>
          <button type="button" onClick={() => set('online_registration', false)} data-testid="registration-contact"
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${form.online_registration === false ? '' : 'border-[#333] bg-[#0a0a0a]'}`}
            style={form.online_registration === false ? { borderColor: accentColor, backgroundColor: `${accentColor}10` } : {}}>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0"
                style={{ borderColor: form.online_registration === false ? accentColor : '#555' }}>
                {form.online_registration === false && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accentColor }} />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" style={{ color: form.online_registration === false ? accentColor : '#666' }} />
                  <span className="text-white font-bold text-sm">Contacter l'organisateur pour s'inscrire</span>
                </div>
                <p className="text-[#888] text-xs mt-1 leading-relaxed">Pas d'inscription en ligne : les joueurs contactent l'organisateur (inscription en boutique, etc.).</p>
              </div>
            </div>
          </button>
        </CardContent>
      </Card>

      {/* Waitlist */}
      {form.online_registration !== false && (
      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardHeader><CardTitle className="text-white flex items-center gap-2"><ListOrdered className="w-5 h-5" style={{ color: accentColor }} /> Liste d'attente (tournoi complet)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-[#666] text-sm">Que se passe-t-il lorsque le tournoi est complet ?</p>
          <button type="button" onClick={() => setWaitlist('manual')} data-testid="waitlist-manual"
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${form.waitlist_mode === 'manual' ? '' : 'border-[#333] bg-[#0a0a0a]'}`}
            style={form.waitlist_mode === 'manual' ? { borderColor: accentColor, backgroundColor: `${accentColor}10` } : {}}>
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0`}
                style={{ borderColor: form.waitlist_mode === 'manual' ? accentColor : '#555' }}>
                {form.waitlist_mode === 'manual' && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accentColor }} />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <ListOrdered className="w-4 h-4" style={{ color: form.waitlist_mode === 'manual' ? accentColor : '#666' }} />
                  <span className="text-white font-bold text-sm">Liste d'attente manuelle</span>
                </div>
                <p className="text-[#888] text-xs mt-1 leading-relaxed">
                  Vous gérez la liste. Si annulation, la place n'est <strong className="text-white">pas libérée automatiquement</strong>.
                  Vous invitez qui vous voulez. Vous pouvez aussi libérer la place manuellement.
                </p>
              </div>
            </div>
          </button>
          <button type="button" onClick={() => setWaitlist('notification')} data-testid="waitlist-notification"
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${form.waitlist_mode === 'notification' ? '' : 'border-[#333] bg-[#0a0a0a]'}`}
            style={form.waitlist_mode === 'notification' ? { borderColor: accentColor, backgroundColor: `${accentColor}10` } : {}}>
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0`}
                style={{ borderColor: form.waitlist_mode === 'notification' ? accentColor : '#555' }}>
                {form.waitlist_mode === 'notification' && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accentColor }} />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4" style={{ color: form.waitlist_mode === 'notification' ? accentColor : '#666' }} />
                  <span className="text-white font-bold text-sm">Notification à tous — premier arrivé, premier servi</span>
                </div>
                <p className="text-[#888] text-xs mt-1 leading-relaxed">
                  Les joueurs cliquent sur <strong className="text-white">"Être notifié si une place se libère"</strong>.
                  Si annulation → notification push → premier inscrit = place obtenue.
                </p>
              </div>
            </div>
          </button>
          <button type="button" onClick={() => setWaitlist('automatic')} data-testid="waitlist-automatic"
            className={`w-full text-left p-4 rounded-xl border-2 transition-all ${form.waitlist_mode === 'automatic' ? '' : 'border-[#333] bg-[#0a0a0a]'}`}
            style={form.waitlist_mode === 'automatic' ? { borderColor: accentColor, backgroundColor: `${accentColor}10` } : {}}>
            <div className="flex items-start gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0`}
                style={{ borderColor: form.waitlist_mode === 'automatic' ? accentColor : '#555' }}>
                {form.waitlist_mode === 'automatic' && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accentColor }} />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <ListOrdered className="w-4 h-4" style={{ color: form.waitlist_mode === 'automatic' ? accentColor : '#666' }} />
                  <span className="text-white font-bold text-sm">Automatique — file séquentielle</span>
                </div>
                <p className="text-[#888] text-xs mt-1 leading-relaxed">
                  Si annulation → le <strong className="text-white">1er de la file</strong> est notifié seul et dispose d'un
                  <strong className="text-white"> délai</strong> pour confirmer. Sans réponse, la place passe automatiquement au suivant.
                </p>
                {form.waitlist_mode === 'automatic' && (
                  <div className="flex items-center gap-2 mt-3" onClick={e => e.stopPropagation()}>
                    <Label className="text-[#B0B0B0] text-sm whitespace-nowrap">Délai de confirmation</Label>
                    <Input type="number" min="5" max="1440" value={form.waitlist_confirm_minutes}
                      onChange={e => set('waitlist_confirm_minutes', parseInt(e.target.value) || 30)}
                      className="bg-[#252525] border-[#333] text-white w-20 text-center" data-testid="waitlist-minutes-input" />
                    <span className="text-[#B0B0B0] text-sm">minutes</span>
                  </div>
                )}
              </div>
            </div>
          </button>
        </CardContent>
      </Card>
      )}

      <ActionButtons />

      {/* ── Code Warning Modal ── */}
      {showCodeWarning && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowCodeWarning(false)}>
          <div className="bg-[#1a1a1a] border border-[#333] rounded-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
              <h3 className="text-lg font-bold text-white">Pas de code tournoi</h3>
            </div>
            <p className="text-[#B0B0B0] text-sm">Aucun code tournoi éditeur n'a été renseigné. Voulez-vous continuer ?</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowCodeWarning(false)} className="border-[#333] text-white">Annuler</Button>
              <Button onClick={proceedToPreview} style={{ backgroundColor: accentColor }} data-testid="code-warning-continue">Continuer sans code</Button>
            </div>
          </div>
        </div>
      )}

      {showNoLimitWarning && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowNoLimitWarning(false)}>
          <div className="bg-[#1a1a1a] border border-[#333] rounded-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="no-limit-dialog">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6" style={{ color: accentColor }} />
              <h3 className="text-lg font-bold text-white">Pas de limite de joueurs</h3>
            </div>
            <p className="text-[#B0B0B0] text-sm">Il n'y a pas de limite de joueurs pour cet évènement.</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={editCapacity} className="border-[#333] text-white" data-testid="no-limit-edit">Modifier</Button>
              <Button onClick={proceedAfterCapacity} style={{ backgroundColor: accentColor }} data-testid="no-limit-ok">OK</Button>
            </div>
          </div>
        </div>
      )}

      {showExceedWarning && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowExceedWarning(false)}>
          <div className="bg-[#1a1a1a] border border-[#333] rounded-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="exceed-dialog">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-yellow-500" />
              <h3 className="text-lg font-bold text-white">Capacité dépassée</h3>
            </div>
            <p className="text-[#B0B0B0] text-sm">La capacité de cet évènement dépasse la limite de la salle{selectedShop?.room_capacity ? ` (${selectedShop.room_capacity} places)` : ''}.</p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={editCapacity} className="border-[#333] text-white" data-testid="exceed-edit">Modifier</Button>
              <Button onClick={proceedAfterCapacity} style={{ backgroundColor: accentColor }} data-testid="exceed-ok">OK</Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preview Modal ── */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-[#1a1a1a] border border-[#333] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-[#333]">
              <h2 className="text-xl font-bold text-white flex items-center gap-2"><Eye className="w-5 h-5" style={{ color: accentColor }} /> Récapitulatif</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                {GAME_LOGOS[form.game] && <img src={GAME_LOGOS[form.game]} alt="" className="h-8 object-contain" />}
                <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: `${accentColor}20`, color: accentColor }}>
                  {form.game === 'autre' ? form.customGame : gameInfo?.name}
                </span>
                {(form.format || form.customFormat) && (
                  <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#252525] text-[#B0B0B0]">
                    {form.format === 'Autre (préciser)' ? form.customFormat : form.format}
                  </span>
                )}
              </div>
              <h3 className="text-lg font-bold text-white">{form.name}</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-[#B0B0B0]">
                    <Calendar className="w-4 h-4" style={{ color: accentColor }} />
                    {form.date} à {form.time}
                  </div>
                  {form.end_date && <div className="flex items-center gap-2 text-[#666]"><Calendar className="w-4 h-4" /> Fin: {form.end_date} {form.end_time}</div>}
                  <div className="flex items-center gap-2 text-[#B0B0B0]">
                    <MapPin className="w-4 h-4" style={{ color: accentColor }} />
                    {form.no_physical_venue ? 'En ligne' : `${form.venue_name}${form.venue_city ? `, ${form.venue_city}` : ''}`}
                  </div>
                </div>
                <div className="space-y-2">
                  {form.capacity && <div className="flex items-center gap-2 text-[#B0B0B0]"><Users className="w-4 h-4" style={{ color: accentColor }} />{form.capacity} joueurs max</div>}
                  <div className="text-[#B0B0B0]">Prix: {form.price ? `${form.price}€` : 'Gratuit'}</div>
                  <div className="text-[#666] text-xs">Remboursement: {getRefundLabel()}</div>
                  {form.tournament_code && <div className="text-[#B0B0B0] text-xs">Code: <span className="text-white font-mono">{form.tournament_code}</span></div>}
                </div>
              </div>
              {form.description && (
                <div className="p-3 rounded-lg bg-[#0a0a0a] border border-[#333]">
                  <div className="text-[#B0B0B0] text-sm [&_a]:text-[#6C63FF] [&_a]:underline" dangerouslySetInnerHTML={{ __html: form.description }} />
                </div>
              )}
              <div className="flex items-center gap-2 p-3 rounded-lg bg-[#0a0a0a] border border-[#333]">
                {form.online_registration !== false
                  ? <><Link2 className="w-4 h-4 shrink-0" style={{ color: accentColor }} /><span className="text-[#B0B0B0] text-sm">Inscriptions : <strong className="text-white">En ligne</strong></span></>
                  : <><Users className="w-4 h-4 shrink-0" style={{ color: accentColor }} /><span className="text-[#B0B0B0] text-sm">Inscriptions : <strong className="text-white">Contacter l'organisateur</strong></span></>
                }
              </div>
              {form.online_registration !== false && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-[#0a0a0a] border border-[#333]">
                {form.waitlist_mode === 'manual'
                  ? <><ListOrdered className="w-4 h-4 shrink-0" style={{ color: accentColor }} /><span className="text-[#B0B0B0] text-sm">Liste d'attente : <strong className="text-white">Manuelle</strong></span></>
                  : form.waitlist_mode === 'automatic'
                  ? <><ListOrdered className="w-4 h-4 shrink-0" style={{ color: accentColor }} /><span className="text-[#B0B0B0] text-sm">Liste d'attente : <strong className="text-white">Automatique</strong> (délai {form.waitlist_confirm_minutes} min)</span></>
                  : <><Bell className="w-4 h-4 shrink-0" style={{ color: accentColor }} /><span className="text-[#B0B0B0] text-sm">Liste d'attente : <strong className="text-white">Notification à tous</strong></span></>
                }
              </div>
              )}
            </div>
            <div className="p-6 border-t border-[#333] flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowPreview(false)} className="border-[#333] text-white gap-2"><ArrowLeft className="w-4 h-4" /> Je modifie</Button>
              <Button onClick={confirmCreate} disabled={loading} style={{ backgroundColor: accentColor }} className="gap-2" data-testid="confirm-create-btn">
                <Check className="w-4 h-4" /> {loading ? 'Création...' : 'Je crée le tournoi'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rich text editor with fixed cursor, emoji search, link at cursor ──
function RichDescription({ value, onChange }) {
  const [showEmoji, setShowEmoji] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const ref = useRef(null);
  const savedSelection = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (ref.current && !initialized.current && value) {
      ref.current.innerHTML = value;
      initialized.current = true;
    }
  }, [value]);

  const EMOJIS = {
    'Jeux': ['⚔️','🏆','🎮','🃏','💎','🎴','🎲','🪄','🛡️','🗡️','🏰','🔮','🐉','🧙','⚡','🌟','🎯','🃑','🦊','🐲'],
    'Tournoi': ['🥇','🥈','🥉','🏅','👑','💪','🎪','📢','🏠','💬','📌','🏷️','💳','🎟️','📋','🗓️','🎉','🎊','🎈','🥳'],
    'Temps': ['📅','⏰','🕐','🕑','🕒','🕓','🕔','🕕','🕖','🕗','🕘','🕙','🕚','🕛','🕜','🕝','🕞','🕟','🕠','🕡'],
    'Symboles': ['✅','❌','⚠️','🆓','💰','🎁','📍','🔥','⭐','💡','🚀','💯','🤝','🙏','👋','😎','🤩','👥','👤','🎭'],
    'Nature': ['🌊','🌿','🌙','☀️','❄️','🌈','🍀','🌸','🌺','🌻','🦁','🐺','🦅','🐍','🦋','🕊️','🐾','🌍','💀','👻'],
    'Divers': ['❤️','💙','💜','💚','🧡','🖤','🤍','💛','💗','💝','🔴','🟠','🟡','🟢','🔵','🟣','⚫','⚪','🟤','🔶'],
  };

  const filteredEmojis = useMemo(() => {
    if (!emojiSearch.trim()) return EMOJIS;
    const q = emojiSearch.toLowerCase();
    const result = {};
    Object.entries(EMOJIS).forEach(([cat, list]) => {
      if (cat.toLowerCase().includes(q)) { result[cat] = list; }
      else {
        const filtered = list.filter(() => cat.toLowerCase().includes(q));
        if (filtered.length) result[cat] = filtered;
      }
    });
    // If search matches nothing by category, show all emojis flat
    if (Object.keys(result).length === 0) {
      result['Résultats'] = Object.values(EMOJIS).flat();
    }
    return result;
  }, [emojiSearch]);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && ref.current?.contains(sel.anchorNode)) {
      savedSelection.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    if (savedSelection.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelection.current);
    }
  };

  const syncContent = () => {
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const execCmd = (cmd, val = null) => {
    ref.current?.focus();
    restoreSelection();
    document.execCommand(cmd, false, val);
    syncContent();
    saveSelection();
  };

  const openLinkDialog = () => {
    saveSelection();
    const sel = window.getSelection();
    if (sel && sel.toString().trim()) {
      setLinkText(sel.toString());
    } else {
      setLinkText('');
    }
    setLinkUrl('');
    setShowLinkInput(true);
  };

  const insertLink = () => {
    if (!linkUrl.trim()) return;
    ref.current?.focus();
    restoreSelection();
    const sel = window.getSelection();
    const text = linkText.trim() || linkUrl;

    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const a = document.createElement('a');
      a.href = linkUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.style.color = '#6C63FF';
      a.style.textDecoration = 'underline';
      a.textContent = text;
      range.insertNode(a);
      // Move cursor after the link
      range.setStartAfter(a);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }

    syncContent();
    setShowLinkInput(false);
    setLinkUrl('');
    setLinkText('');
  };

  const insertEmoji = (emoji) => {
    ref.current?.focus();
    restoreSelection();
    document.execCommand('insertText', false, emoji);
    syncContent();
    saveSelection();
  };

  return (
    <div className="border border-[#333] rounded-lg overflow-visible relative">
      <div className="flex flex-wrap items-center gap-1 p-2 bg-[#0a0a0a] border-b border-[#333]">
        <button type="button" onMouseDown={e => { e.preventDefault(); execCmd('bold'); }} className="p-1.5 rounded hover:bg-[#333] text-white font-bold text-sm w-8 h-8 flex items-center justify-center" title="Gras (Ctrl+B)">B</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); execCmd('italic'); }} className="p-1.5 rounded hover:bg-[#333] text-white italic text-sm w-8 h-8 flex items-center justify-center" title="Italique (Ctrl+I)">I</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); execCmd('underline'); }} className="p-1.5 rounded hover:bg-[#333] text-white underline text-sm w-8 h-8 flex items-center justify-center" title="Souligné (Ctrl+U)">U</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); execCmd('strikeThrough'); }} className="p-1.5 rounded hover:bg-[#333] text-white line-through text-sm w-8 h-8 flex items-center justify-center" title="Barré">S</button>
        <div className="w-px h-5 bg-[#333] mx-1" />
        <button type="button" onMouseDown={e => { e.preventDefault(); execCmd('insertUnorderedList'); }} className="p-1.5 rounded hover:bg-[#333] text-white text-xs w-8 h-8 flex items-center justify-center" title="Liste">•</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); execCmd('insertOrderedList'); }} className="p-1.5 rounded hover:bg-[#333] text-white text-xs w-8 h-8 flex items-center justify-center" title="Numérotée">1.</button>
        <div className="w-px h-5 bg-[#333] mx-1" />
        {/* Link */}
        <div className="relative">
          <button type="button" onMouseDown={e => { e.preventDefault(); saveSelection(); openLinkDialog(); }}
            className="p-1.5 rounded hover:bg-[#333] text-white w-8 h-8 flex items-center justify-center" title="Lien">
            <Link2 className="w-4 h-4" />
          </button>
          {showLinkInput && (
            <div className="absolute top-full left-0 mt-1 p-3 bg-[#1a1a1a] border border-[#333] rounded-lg shadow-xl z-50 w-[280px] space-y-2">
              <input value={linkText} onChange={e => setLinkText(e.target.value)} placeholder="Texte du lien"
                className="w-full bg-[#252525] border border-[#333] rounded px-2 py-1.5 text-white text-xs outline-none" />
              <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://..."
                className="w-full bg-[#252525] border border-[#333] rounded px-2 py-1.5 text-white text-xs outline-none"
                onKeyDown={e => { if (e.key === 'Enter') insertLink(); }} autoFocus />
              <div className="flex gap-2">
                <button type="button" onClick={insertLink} className="px-3 py-1 bg-[#6C63FF] text-white text-xs rounded font-medium">Insérer</button>
                <button type="button" onClick={() => setShowLinkInput(false)} className="px-3 py-1 text-[#666] text-xs">Annuler</button>
              </div>
            </div>
          )}
        </div>
        {/* Emoji */}
        <div className="relative">
          <button type="button" onMouseDown={e => { e.preventDefault(); saveSelection(); setShowEmoji(!showEmoji); setEmojiSearch(''); }}
            className="p-1.5 rounded hover:bg-[#333] text-white w-8 h-8 flex items-center justify-center" title="Emojis">😊</button>
          {showEmoji && (
            <div className="absolute top-full right-0 mt-1 bg-[#1a1a1a] border border-[#333] rounded-lg shadow-xl z-50 w-[340px]">
              <div className="p-2 border-b border-[#333]">
                <div className="flex items-center gap-2 bg-[#252525] rounded px-2 border border-[#333]">
                  <Search className="w-3.5 h-3.5 text-[#666]" />
                  <input value={emojiSearch} onChange={e => setEmojiSearch(e.target.value)}
                    placeholder="Rechercher..." className="bg-transparent text-white text-xs py-1.5 outline-none flex-1" autoFocus />
                </div>
              </div>
              <div className="p-2 max-h-[280px] overflow-y-auto">
                {Object.entries(filteredEmojis).map(([cat, list]) => (
                  <div key={cat} className="mb-2">
                    <p className="text-[#666] text-[10px] font-bold uppercase tracking-wider mb-1">{cat}</p>
                    <div className="grid grid-cols-10 gap-0.5">
                      {list.map((e, i) => (
                        <button key={i} type="button" onClick={() => { insertEmoji(e); setShowEmoji(false); }}
                          className="p-1 text-lg hover:bg-[#333] rounded text-center leading-none">{e}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning
        className="min-h-[120px] p-3 bg-[#252525] text-white text-sm outline-none [&_a]:text-[#6C63FF] [&_a]:underline"
        onInput={syncContent} onBlur={() => { saveSelection(); syncContent(); }}
        onMouseUp={saveSelection} onKeyUp={saveSelection}
        data-testid="description-editor" />
    </div>
  );
}
